import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { sendMagicLink, startGoogleOAuth } from "../lib/api";
import { trackEvent } from "../lib/analytics";

// Minimal Google G-logo — inline SVG avoids extra network fetch and matches Koyfin's style.
function GoogleG({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!validEmail || busy) return;
    setBusy(true); setError("");
    try {
      await sendMagicLink(email);
      trackEvent("magic_link_requested", { email_domain: email.split("@")[1] });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    setError(""); setBusy(true);
    try {
      trackEvent("oauth_started", { provider: "google" });
      const { url } = await startGoogleOAuth();
      window.location.href = url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not start Google sign-in.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col px-6 pb-24 pt-16 sm:pt-24">
        {sent ? (
          <div data-testid="magic-link-sent" className="rounded-2xl border border-[#e8e6df] bg-white p-8 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[#eaf6f1]">
              <CheckCircle2 className="h-6 w-6 text-[#157a59]" />
            </div>
            <h1 className="mt-5 font-heading text-2xl font-bold tracking-tight text-[#1a1a18]">Check your inbox</h1>
            <p className="mt-2 text-sm leading-relaxed text-neutral-600">
              We sent a sign-in link to <span className="font-semibold text-[#1a1a18]">{email}</span>.
              Click the link in the email to sign in. It's valid for 15 minutes.
            </p>
            <button
              onClick={() => { setSent(false); setEmail(""); }}
              data-testid="magic-link-back"
              className="mt-6 text-sm font-medium text-neutral-600 underline-offset-4 hover:text-[#1a1a18] hover:underline"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-[#1a1a18] sm:text-4xl">
              Sign in
              <br />
              or create your account
            </h1>
            <p className="mt-2 text-sm text-neutral-500">
              Enter your work email — same form for both. New here? An account will be created for you.
            </p>

            <form onSubmit={submitEmail} className="mt-8 space-y-3" noValidate>
              <label htmlFor="email" className="block text-sm font-semibold text-[#1a1a18]">
                Work email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-[#dcd9cf] bg-white py-3 pl-10 pr-4 text-sm outline-none transition-colors focus:border-[#1a1a18] focus:ring-1 focus:ring-[#1a1a18]"
                  autoFocus
                />
              </div>
              <p className="text-xs text-neutral-500">
                By proceeding, you confirm that you have read and agree to our{" "}
                <a href="/terms" className="underline underline-offset-2 hover:text-[#1a1a18]">Terms of Use</a>{" "}
                and{" "}
                <a href="/privacy" className="underline underline-offset-2 hover:text-[#1a1a18]">Privacy Policy</a>.
              </p>
              <button
                type="submit"
                disabled={!validEmail || busy}
                data-testid="login-continue-btn"
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#1a1a18] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#2a2a26] disabled:cursor-not-allowed disabled:bg-neutral-300"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue with email <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>

            <div className="my-8 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-neutral-400">
              <div className="h-px flex-1 bg-[#e8e6df]" />
              or
              <div className="h-px flex-1 bg-[#e8e6df]" />
            </div>

            <button
              onClick={google}
              disabled={busy}
              data-testid="login-google-btn"
              className="inline-flex w-full items-center justify-center gap-3 rounded-lg border border-[#dcd9cf] bg-white px-5 py-3 text-sm font-semibold text-[#1a1a18] transition-colors hover:border-neutral-500 hover:bg-[#f5f4ef] disabled:opacity-60"
            >
              <GoogleG /> Sign in with Google
            </button>

            {error && (
              <div data-testid="login-error" className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <p className="mt-10 text-center text-xs text-neutral-500">
              Trouble signing in? <a href="/contact" className="underline underline-offset-2 hover:text-[#1a1a18]">Contact support</a>
            </p>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
